/**
 * Deployment registry for the research dataset dashboard.
 *
 * Each deployment represents one classroom session where VizPI was active.
 * The time windows are used to filter telemetry events to the correct
 * class period (same logic as TelemetryDataContext's SESSION_TIME_WINDOWS).
 *
 * sessionId  — the VizPI session ID (used in API calls)
 * sectionId  — the course section ID (used for time-window matching)
 *
 * Deployments 9–12 are week-long asynchronous extra-credit windows
 * (CS 3114 summer), not single class periods: their `window` spans
 * release 00:00 ET through the 11:59:59 PM ET deadline, so telemetry
 * filtering works unchanged. Their summer sectionId is not yet known
 * (null TODO). Language is 'Java' per the task designs — note
 * deployment_6 lists spring CS 3114 as 'C'; reconcile before the
 * dataset ships.
 */

export const DEPLOYMENTS = [
  {
    id: 'deployment_1',
    label: 'Instructor A – AM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-02-26',
    time: '9:30–10:45 AM EST',
    sessionId: '654',
    sectionId: '13371',
    window: {
      start: new Date('2026-02-26T14:30:00Z').getTime(),
      end:   new Date('2026-02-26T16:15:00Z').getTime(),
    },
  },
  {
    id: 'deployment_2',
    label: 'Instructor A – PM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-02-26',
    time: '2:00–3:15 PM EST',
    sessionId: '654',
    sectionId: '21938',
    window: {
      start: new Date('2026-02-26T19:00:00Z').getTime(),
      end:   new Date('2026-02-26T20:45:00Z').getTime(),
    },
  },
  {
    id: 'deployment_3',
    label: 'Instructor B – AM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-03-18',
    time: '9:05–9:55 AM EDT',
    sessionId: '661',
    sectionId: '13370',
    window: {
      start: new Date('2026-03-18T13:05:00Z').getTime(),
      end:   new Date('2026-03-18T14:25:00Z').getTime(),
    },
  },
  {
    id: 'deployment_13',
    label: 'Instructor B – PM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-03-18',
    time: '12:20–1:10 PM EDT',
    sessionId: '661',
    sectionId: '13372', // TODO: verify — inferred from Instructor B's PM section on 4/27
    window: {
      start: new Date('2026-03-18T16:20:00Z').getTime(),
      end:   new Date('2026-03-18T17:40:00Z').getTime(),
    },
  },
  {
    id: 'deployment_4',
    label: 'Instructor B – AM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-04-27',
    time: '9:05–9:55 AM EDT',
    sessionId: '643',
    sectionId: '13370',
    window: {
      start: new Date('2026-04-27T13:05:00Z').getTime(),
      end:   new Date('2026-04-27T14:25:00Z').getTime(),
    },
  },
  {
    id: 'deployment_5',
    label: 'Instructor B – PM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-04-27',
    time: '12:20–1:10 PM EDT',
    sessionId: '643',
    sectionId: '13372',
    window: {
      start: new Date('2026-04-27T16:20:00Z').getTime(),
      end:   new Date('2026-04-27T17:40:00Z').getTime(),
    },
  },
  {
    id: 'deployment_6',
    label: 'Instructor C – PM',
    course: 'CS 3114',
    language: 'C',
    date: '2026-04-27',
    time: '5:30–6:45 PM EDT',
    sessionId: '662',
    sectionId: '13369',
    window: {
      start: new Date('2026-04-27T21:30:00Z').getTime(),
      end:   new Date('2026-04-27T23:15:00Z').getTime(),
    },
  },
  {
    id: 'deployment_7',
    label: 'Instructor A – AM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-04-28',
    time: '9:30–10:45 AM EDT',
    sessionId: '643',
    sectionId: '13371',
    window: {
      start: new Date('2026-04-28T13:30:00Z').getTime(),
      end:   new Date('2026-04-28T15:15:00Z').getTime(),
    },
  },
  {
    id: 'deployment_8',
    label: 'Instructor A – PM',
    course: 'CS 2064',
    language: 'Python',
    date: '2026-04-28',
    time: '2:00–3:15 PM EDT',
    sessionId: '643',
    sectionId: '21938',
    window: {
      start: new Date('2026-04-28T18:00:00Z').getTime(),
      end:   new Date('2026-04-28T19:45:00Z').getTime(),
    },
  },
  {
    id: 'deployment_9',
    label: 'Instructor C – Async',
    course: 'CS 3114',
    language: 'Java',
    // Hash Tables — linear probing insert, search, delete
    date: '2026-07-20',
    time: 'Async extra credit — due Sun Jul 26, 11:59 PM EDT',
    sessionId: '674',
    sectionId: null, // TODO: summer CS 3114 section
    window: {
      start: new Date('2026-07-20T04:00:00Z').getTime(),
      end:   new Date('2026-07-27T03:59:59Z').getTime(),
    },
  },
  {
    id: 'deployment_10',
    label: 'Instructor C – Async',
    course: 'CS 3114',
    language: 'Java',
    // Graph Traversal — depth-first search
    date: '2026-07-27',
    time: 'Async extra credit — due Sun Aug 2, 11:59 PM EDT',
    sessionId: '675',
    sectionId: null, // TODO: summer CS 3114 section
    window: {
      start: new Date('2026-07-27T04:00:00Z').getTime(),
      end:   new Date('2026-08-03T03:59:59Z').getTime(),
    },
  },
  {
    id: 'deployment_11',
    label: 'Instructor C – Async',
    course: 'CS 3114',
    language: 'Java',
    // General Trees — height and leaf count
    date: '2026-08-03',
    time: 'Async extra credit — due Sun Aug 9, 11:59 PM EDT',
    sessionId: '676',
    sectionId: null, // TODO: summer CS 3114 section
    window: {
      start: new Date('2026-08-03T04:00:00Z').getTime(),
      end:   new Date('2026-08-10T03:59:59Z').getTime(),
    },
  },
  {
    id: 'deployment_12',
    label: 'Instructor C – Async',
    course: 'CS 3114',
    language: 'Java',
    // Binary Search Trees — recursive range-count on a BST
    date: '2026-08-06',
    time: 'Async extra credit — due Thu Aug 13, 11:59 PM EDT',
    sessionId: '677',
    sectionId: null, // TODO: summer CS 3114 section
    window: {
      start: new Date('2026-08-06T04:00:00Z').getTime(),
      end:   new Date('2026-08-14T03:59:59Z').getTime(),
    },
  },
]

export default DEPLOYMENTS